// src/components/common/Header.jsx
import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useWallet } from '../../contexts/WalletContext';
import './Header.css';
import { isRegistrationOpen } from '../../config/environment';
import { notificationService } from '../../services/notificationInboxService';
import { useWebSocket } from '../../contexts/WebSocketContext';
import ChangePasswordModal from './ChangePasswordModal';

const Header = () => {
  const { currentUser, isAdmin, logout } = useAuth();
  const { socket } = useWebSocket();
  const { 
    walletData,
    refreshWallet, 
    loading,
    error,
    clearError
  } = useWallet();
  const location = useLocation();
  const navigate = useNavigate();
  const [showDropdown, setShowDropdown] = useState(false);
  const dropdownRef = useRef(null);
  const [inviteCopied, setInviteCopied] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [showNotif, setShowNotif] = useState(false);
  const notifRef = useRef(null);
  const [showChangePassword, setShowChangePassword] = useState(false);

  // Referral invite link (redirects to /register with the referrer's ID).
  const referralLink = currentUser?.id
    ? `${window.location.origin}/register?ref=${currentUser.id}`
    : '';

  // Copy the referral link and briefly show a "copied" toast.
  const copyInviteLink = async () => {
    try {
      await navigator.clipboard.writeText(referralLink);
    } catch (e) {
      const ta = document.createElement('textarea');
      ta.value = referralLink;
      document.body.appendChild(ta);
      ta.select();
      document.execCommand('copy');
      document.body.removeChild(ta);
    }
    setInviteCopied(true);
    setTimeout(() => setInviteCopied(false), 2000);
  };

  // Initial wallet load
  useEffect(() => {
    if (currentUser) {
      console.log('👤 Header: User detected, loading wallet...');
      refreshWallet();
    }
  }, [currentUser]);

  // Debug logging for currentUser and walletData
  useEffect(() => {
    console.log('🔍 DEBUG - Auth Context User:', {
      id: currentUser?.id,
      username: currentUser?.username,
      country: currentUser?.country,
      countryName: currentUser?.countryName,
      email: currentUser?.email
    });
  }, [currentUser]);

  useEffect(() => {
    if (walletData) {
      console.log('💰 DEBUG - Wallet Context Data:', {
        countryCode: walletData.countryCode,
        countryName: walletData.countryName,
        balances: walletData.balances,
        primaryCurrency: walletData.primaryCurrency,
        currencySymbol: walletData.currencySymbol
      });
    }
  }, [walletData]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowDropdown(false);
      }
      if (notifRef.current && !notifRef.current.contains(event.target)) {
        setShowNotif(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const isActive = (path) => {
    return location.pathname === path ? 'active' : '';
  };

  const handleAddFunds = () => {
    navigate('/add-funds');
    setShowDropdown(false);
  };

  const handleProfile = () => {
    navigate('/profile');
    setShowDropdown(false);
  };

  const handleTransactionHistory = () => {
    navigate('/transaction-history');
    setShowDropdown(false);
  };

  const handleChangePassword = () => {
    setShowDropdown(false);
    setShowChangePassword(true);
  };

  const loadNotifications = async () => {
    try {
      const [list, count] = await Promise.all([notificationService.list(), notificationService.unreadCount()]);
      setNotifications(list);
      setUnreadCount(count);
    } catch (e) { /* ignore */ }
  };

  const markNotifRead = async (id) => {
    try { await notificationService.markRead(id); loadNotifications(); } catch (e) { /* ignore */ }
  };

  const markAllNotifRead = async () => {
    try { await notificationService.markAllRead(); loadNotifications(); } catch (e) { /* ignore */ }
  };

  // Real-time notification push via WebSocket (instant bell updates).
  useEffect(() => {
    if (!socket) return;
    const onNotif = () => loadNotifications();
    socket.on('notification', onNotif);
    socket.on('notification-unread', onNotif);
    return () => {
      socket.off('notification', onNotif);
      socket.off('notification-unread', onNotif);
    };
  }, [socket]);

  useEffect(() => {
    if (currentUser) {
      loadNotifications();
      const iv = setInterval(loadNotifications, 30000);
      return () => clearInterval(iv);
    }
  }, [currentUser]);

  // Format practice chips
  const formatChips = (amount) => {
    return new Intl.NumberFormat('en-US').format(amount);
  };

  // Get practice chips based on user level
  const getPracticeChips = () => {
    return currentUser ? 10000 + (currentUser.level || 0) * 1000 : 0;
  };

  const handleManualRefresh = async () => {
    console.log('🔄 Header: Manual refresh requested');
    try {
      await refreshWallet();
    } catch (err) {
      console.error('Refresh failed:', err);
    }
  };

  // Get user's country code from walletData (preferred) or currentUser
  const getUserCountryCode = () => {
    // First try walletData (from server)
    if (walletData?.countryCode) {
      return walletData.countryCode.toUpperCase();
    }
    // Fallback to currentUser
    if (currentUser?.country) {
      return currentUser.country.toUpperCase();
    }
    return 'US'; // Default
  };

  // Get user's country name from walletData (preferred) or currentUser
  const getUserCountryName = () => {
    if (walletData?.countryName) {
      return walletData.countryName;
    }
    if (currentUser?.countryName) {
      return currentUser.countryName;
    }
    return 'United States';
  };

  // Check if user is from Liberia
  const isLiberiaUser = () => {
    const countryCode = getUserCountryCode();
    return countryCode === 'LR';
  };

  // Check if user is from USA
  const isUSAUser = () => {
    const countryCode = getUserCountryCode();
    return countryCode === 'US';
  };

  // Check if user is from India
  const isIndiaUser = () => {
    const countryCode = getUserCountryCode();
    return countryCode === 'IN';
  };

  // Helper function to determine local currency display
 
const getLocalCurrencyDisplay = () => {
  const countryCode = getUserCountryCode();
  
  console.log('🔤 getLocalCurrencyDisplay - Country Code:', countryCode);
  console.log('🔤 getLocalCurrencyDisplay - Current User:', {
    currencyCode: currentUser?.currencyCode,
    currency: currentUser?.currency
  });
  console.log('🔤 getLocalCurrencyDisplay - Wallet Data:', {
    primaryCurrency: walletData?.primaryCurrency,
    currencySymbol: walletData?.currencySymbol
  });
  
  // First, try to get the currency from currentUser (from auth context)
  const userCurrency = currentUser?.currencyCode || currentUser?.currency;
  
  // If we have user's currency, use it
  if (userCurrency && userCurrency !== 'USD') {
    return userCurrency;
  }
  
  // Special handling for specific countries
  if (countryCode === 'BJ') {
    return 'CFA';
  }
  if (countryCode === 'LR' || countryCode === 'LBR') {
    return 'LRD';
  }
  if (countryCode === 'GM') {
    return 'GMD'; // Gambia
  }
  if (countryCode === 'IN') {
    return 'INR';
  }
  
  // Try walletData primaryCurrency (this should come from the server)
  if (walletData?.primaryCurrency && walletData.primaryCurrency !== 'USD') {
    return walletData.primaryCurrency;
  }
  
  // If all else fails, use country code as currency code
  // Or return a default based on country
  return 'LOCAL';
};

  // Get formatted balance for a specific currency
  const getFormattedBalance = (currency) => {
    if (!walletData?.balances) return '0.00';
    
    const balance = walletData.balances[currency] || 0;
    
    // Format based on currency type
    switch (currency) {
      case 'USD':
        return `$${balance.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
      
      case 'INR':
        return `₹${balance.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
      
      case 'LRD':
        return `L$${balance.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
      
      case 'LOCAL':
        const localCurrency = getLocalCurrencyDisplay();
        console.log('💵 LOCAL Balance Display - Currency:', localCurrency, 'Balance:', balance);
        if (localCurrency === 'CFA') {
          return `${balance.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} CFA`;
        }
        return `${balance.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ${localCurrency}`;
      
      default:
        return `${balance} ${currency}`;
    }
  };

  // Get the actual LOCAL balance value from walletData
  const getLocalBalanceValue = () => {
    if (!walletData?.balances) return 0;
    
    // Try to get LOCAL balance
    if (walletData.balances.LOCAL !== undefined) {
      return walletData.balances.LOCAL;
    }
    
    // Fallback: Try to extract from other currency fields
    const countryCode = getUserCountryCode();
    
    if (countryCode === 'LR') {
      return walletData.balances.LRD || 0;
    }
    
    // For other countries, check if there's a balance for their currency
    const localCurrency = getLocalCurrencyDisplay();
    if (localCurrency && walletData.balances[localCurrency] !== undefined) {
      return walletData.balances[localCurrency];
    }
    
    return 0;
  };

  return (
    <header className="header">
      {/* Error Banner */}
      {error && (
        <div className="error-banner">
          <span className="error-message">{error}</span>
          <button className="error-retry-btn" onClick={refreshWallet}>
            Retry
          </button>
          <button className="error-dismiss-btn" onClick={clearError}>
            ×
          </button>
        </div>
      )}

      {/* Top Section - Logo, Tabs, and User */}
      <div className="header-top">
        {/* Logo Section */}
        <div className="logo-section">
          <div className="logo">♠️♥️♣️♦️</div>
          <h1 className="site-name">JV Games</h1>
        </div>
        
        {/* Centered Navigation Tabs */}
        <nav className="header-nav">
          <Link to="/multiplayer" className={`nav-tab ${isActive('/multiplayer')}`}>
            Multiplayer
          </Link>
          <Link to="/cash-games" className={`nav-tab ${isActive('/cash-games')}`}>
            Cash Games
          </Link>
          <Link to="/tournaments" className={`nav-tab ${isActive('/tournaments')}`}>
            Tournaments
          </Link>
          <Link to="/practice-games" className={`nav-tab ${isActive('/practice-games')}`}>
            Practice
          </Link>
          <Link to="/how-to-play" className={`nav-tab ${isActive('/how-to-play')}`}>
            How to Play
          </Link>
        </nav>

        {/* Notification Bell */}
        {currentUser && (
          <div className="notif-section" ref={notifRef}>
            <button className="notif-bell" onClick={() => setShowNotif(!showNotif)} title="Notifications">
              🔔
              {unreadCount > 0 && <span className="notif-badge">{unreadCount > 99 ? '99+' : unreadCount}</span>}
            </button>
            {showNotif && (
              <div className="notif-dropdown">
                <div className="notif-header">
                  <strong>Notifications</strong>
                  {unreadCount > 0 && <button className="notif-mark-all" onClick={markAllNotifRead}>Mark all read</button>}
                </div>
                {notifications.length === 0 ? (
                  <div className="notif-empty">No notifications yet</div>
                ) : (
                  notifications.map((n) => (
                    <div key={n.id} className={`notif-item ${n.is_read ? '' : 'unread'}`} onClick={() => markNotifRead(n.id)}>
                      <div className="notif-title">{n.title}</div>
                      {n.message && <div className="notif-message">{n.message}</div>}
                      <div className="notif-time">{new Date(n.created_at).toLocaleString()}</div>
                    </div>
                  ))
                )}
              </div>
            )}
          </div>
        )}

        {/* User Welcome and Dropdown */}
        {currentUser && (
          <div className="user-section" ref={dropdownRef}>
            <div 
              className="user-welcome"
              onClick={() => setShowDropdown(!showDropdown)}
            >
              <div className="user-avatar-small">
                {currentUser?.username?.[0]?.toUpperCase() || 'P'}
              </div>
              <span className="welcome-text">
                {currentUser?.displayName || currentUser?.username || 'Player'}
              </span>
              {isAdmin && isAdmin() && <span className="admin-badge">Admin</span>}
              <span className="dropdown-arrow">▼</span>
            </div>
            
            {showDropdown && (
              <div className="dropdown-menu">
                <div className="dropdown-header">
                  <div className="user-info">
                    <strong>{currentUser?.displayName || currentUser?.username}</strong>
                    <span className="user-email">{currentUser?.email}</span>
                    <span className="user-id">ID: {currentUser?.id}</span>
                    <span className="user-country">
                      Country: {getUserCountryName()} ({getUserCountryCode()})
                    </span>
                  </div>
                </div>
                
                <div className="dropdown-divider"></div>
                
                <button onClick={handleProfile} className="dropdown-item">
                  <i className="fas fa-user"></i>
                  Profile & Settings
                </button>
                <button onClick={handleAddFunds} className="dropdown-item">
                  <i className="fas fa-plus-circle"></i>
                  Add Funds
                </button>
                <button onClick={handleTransactionHistory} className="dropdown-item">
                  <i className="fas fa-history"></i>
                  Transaction History
                </button>
                <button onClick={handleChangePassword} className="dropdown-item">
                  <i className="fas fa-key"></i>
                  Change Password
                </button>
                
                <div className="dropdown-divider"></div>
                
                <div className="dropdown-actions">
                  <button onClick={handleManualRefresh} className="action-btn refresh" disabled={loading}>
                    <i className={`fas fa-sync ${loading ? 'fa-spin' : ''}`}></i>
                    Refresh Wallet
                  </button>
                </div>
                
                <div className="dropdown-divider"></div>
                
                <button onClick={handleLogout} className="dropdown-item logout">
                  <i className="fas fa-sign-out-alt"></i>
                  Logout
                </button>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Balance Display - Only show when user is logged in */}
      {currentUser && (
        <div className="balance-section">
          {/* Group 1: Referral ID + Invite a friend (way left) */}
          {currentUser?.id && (
            <div className="balance-group ref-group">
              <div className="ref-id-block">
                <span className="group-label">Referral ID:</span>
                <span className="amount referral-code">
                  {currentUser.id}
                  <button
                    className="copy-ref-btn"
                    title="Copy referral code"
                    onClick={() => {
                      try {
                        navigator.clipboard.writeText(String(currentUser.id));
                      } catch (e) { /* ignore */ }
                    }}
                  >
                    <i className="fas fa-copy"></i>
                  </button>
                </span>
              </div>
              {isRegistrationOpen() && (
                <div className="ref-invite-block">
                  <button className="invite-friend-btn" onClick={copyInviteLink}>
                    Invite a friend
                  </button>
                  <span className="invite-bonus-text">get a bonus</span>
                </div>
              )}
            </div>
          )}

          <div className="balance-separator"></div>

          {/* Group 2: Normal currencies (center) */}
          <div className="balance-group currency-group">
            <div className="balance-item currency-usd">
              <span className="currency">USD:</span>
              <span className="amount">
                {loading ? <span className="balance-loading">...</span> : getFormattedBalance('USD')}
              </span>
            </div>
            <div className="balance-item currency-inr">
              <span className="currency">INR:</span>
              <span className="amount">
                {loading ? <span className="balance-loading">...</span> : getFormattedBalance('INR')}
              </span>
            </div>
            {isLiberiaUser() && walletData?.balances?.LRD !== undefined && (
              <div className="balance-item currency-lrd">
                <span className="currency">LRD:</span>
                <span className="amount">
                  {loading ? <span className="balance-loading">...</span> : getFormattedBalance('LRD')}
                </span>
              </div>
            )}
            {!isUSAUser() && !isIndiaUser() && !isLiberiaUser() && (
              <div className="balance-item currency-local">
                <span className="currency">{getLocalCurrencyDisplay()}:</span>
                <span className="amount">
                  {loading ? (
                    <span className="balance-loading">...</span>
                  ) : walletData?.balances?.LOCAL !== undefined ? (
                    getFormattedBalance('LOCAL')
                  ) : getLocalBalanceValue() > 0 ? (
                    `${getLocalBalanceValue().toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ${getLocalCurrencyDisplay()}`
                  ) : (
                    '0.00 ' + getLocalCurrencyDisplay()
                  )}
                </span>
              </div>
            )}
          </div>

          <div className="balance-separator"></div>

          {/* Group 3: Bonus & Loyalty (way right) */}
          <div className="balance-group bonus-group">
            <div className="balance-item currency-bonus">
              <span className="currency">Bonus:</span>
              <span className="amount">
                {loading ? <span className="balance-loading">...</span> : `$${(walletData?.balances?.BONUS || 0).toFixed(2)}`}
              </span>
            </div>
            <div className="balance-item currency-loyalty">
              <span className="currency">Loyalty:</span>
              <span className="amount">
                {loading ? <span className="balance-loading">...</span> : `$${(walletData?.balances?.LOYALTY || 0).toFixed(2)}`}
              </span>
            </div>
            <button
              className="refresh-balance-btn"
              onClick={handleManualRefresh}
              title="Refresh All Balances"
              disabled={loading}
            >
              {loading ? (
                <i className="fas fa-spinner fa-spin"></i>
              ) : (
                <i className="fas fa-sync-alt"></i>
              )}
            </button>
          </div>
        </div>
      )}

      {/* Copied toast */}
      {inviteCopied && (
        <div className="invite-toast">
          ✓ Referral link copied! Paste it anywhere.
        </div>
      )}

      {/* Change password modal */}
      {showChangePassword && (
        <ChangePasswordModal onClose={() => setShowChangePassword(false)} />
      )}
    </header>
  );
};

export default Header;